import React, {FC, ReactNode, useMemo, useState} from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  Typography,
} from "@material-ui/core";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import clsx from "clsx";
import {
  ITranslationConflict,
  ITranslationConflictCandidate,
  ITranslationConflictResolution,
} from "@evennt-tech/api-v2-external";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import Alert from "view/components/Alert";
import CountryFlag from "view/components/CountryFlag";
import FlexBox from "view/components/Layout/FlexBox";
import type {ShowLanguage} from "view/components/SettingsRows/ShowLanguagesRow/useShowLanguages";
import useStyles from "./styles";

export const TRANSLATION_CONFLICT_TITLE = "Translation conflict";
export const TRANSLATION_CONFLICT_ALERT =
  "These translations differ between the Library and this Show. Choose which one to keep.";
export const KEEP_LIBRARY_TRANSLATION = "Keep Library translation";
export const USE_SHOW_TRANSLATION = "Use this Show's translation";

/** Longer values are cut in the entry heading and offer `Show more`. */
const HEADING_VALUE_LIMIT = 60;

type Props = {
  open?: boolean;
  conflicts: ITranslationConflict[];
  /** Flag and display name per code; a code with no entry falls back to itself. */
  languages: ShowLanguage[];
  /** Field → heading label (`name` → `Name`). Unlisted fields show the field name. */
  fieldLabels?: Record<string, string>;
  alert?: ReactNode;
  mergeLabel?: string;
  /**
   * The Show the modal was opened from. Its candidate reads
   * `Use this Show's translation` (FS-2999 §6); every other Show — and every
   * Show when merging at Library level — is named (AC 56).
   */
  currentShowId?: string;
  onClose: () => void;
  onMerge: (
    resolutions: ITranslationConflictResolution[]
  ) => Promise<void> | void;
};

/**
 * Which candidate the merge would keep with no choice made: the Show's value
 * when the Library is one side (the design preselects it), otherwise the
 * first candidate, which is the earliest Show (FS-2999 §7).
 */
export const defaultCandidateIndex = (
  candidates: ITranslationConflictCandidate[]
): number => {
  const hasLibrary = candidates.some((candidate) => candidate.showId === null);
  if (!hasLibrary) return 0;

  const firstShow = candidates.findIndex(
    (candidate) => candidate.showId !== null
  );
  return firstShow === -1 ? 0 : firstShow;
};

export const useShowTranslationLabel = (showName: string): string =>
  `Use ${showName}'s translation`;

/**
 * The Show the modal was opened from reads `Use this Show's translation`
 * (FS-2999 §6); any other Show — every Show at Library level — is named,
 * as AC 56 asks.
 */
const candidateLabel = (
  candidate: ITranslationConflictCandidate,
  currentShowId?: string
): string => {
  if (candidate.showId === null) return KEEP_LIBRARY_TRANSLATION;
  if (candidate.showId === currentShowId) return USE_SHOW_TRANSLATION;
  return candidate.showName
    ? useShowTranslationLabel(candidate.showName)
    : USE_SHOW_TRANSLATION;
};

const TranslationConflictModal: FC<Props> = ({
  open = true,
  conflicts,
  languages,
  fieldLabels,
  alert = TRANSLATION_CONFLICT_ALERT,
  mergeLabel = "Merge",
  currentShowId,
  onClose,
  onMerge,
}) => {
  const classes = useStyles();
  const [selected, setSelected] = useState<number[]>(() =>
    conflicts.map((conflict) => defaultCandidateIndex(conflict.candidates))
  );
  const [expandedHeadings, setExpandedHeadings] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const languageByCode = useMemo(
    () => new Map(languages.map((language) => [language.code, language])),
    [languages]
  );

  const isNumbered = conflicts.length > 1;

  const toggleHeading = (index: number) =>
    setExpandedHeadings((prev) =>
      prev.includes(index)
        ? prev.filter((value) => value !== index)
        : [...prev, index]
    );

  const merge = async () => {
    setIsSubmitting(true);
    try {
      await onMerge(
        conflicts.map((conflict, index) => ({
          field: conflict.field,
          languageCode: conflict.languageCode,
          text: conflict.candidates[selected[index] ?? 0].text,
        }))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeading = (conflict: ITranslationConflict, index: number) => {
    const label = fieldLabels?.[conflict.field] ?? conflict.field;
    const isLong = conflict.sourceText.length > HEADING_VALUE_LIMIT;
    const isExpanded = expandedHeadings.includes(index);
    const value =
      isLong && !isExpanded
        ? `${conflict.sourceText.slice(0, HEADING_VALUE_LIMIT).trimEnd()}…`
        : conflict.sourceText;

    return (
      <FlexBox alignCenter gap={1} className={classes.conflictHeading}>
        <Typography variant="body2" className={classes.conflictHeadingText}>
          {isNumbered && `${index + 1}. `}Translation for {label}: {value}
        </Typography>
        {isLong && (
          <Button
            size="small"
            color="primary"
            className={classes.conflictShowMore}
            onClick={() => toggleHeading(index)}
          >
            {isExpanded ? "Show less" : "Show more"}
          </Button>
        )}
      </FlexBox>
    );
  };

  const renderCandidate = (
    conflict: ITranslationConflict,
    conflictIndex: number,
    candidate: ITranslationConflictCandidate,
    candidateIndex: number
  ) => {
    const isSelected = selected[conflictIndex] === candidateIndex;
    const language = languageByCode.get(conflict.languageCode);

    return (
      <Box
        key={`${candidate.showId ?? "library"}-${candidateIndex}`}
        className={classes.conflictCandidate}
      >
        <FormControlLabel
          control={
            <Radio
              color="primary"
              checked={isSelected}
              onChange={() =>
                setSelected((prev) => {
                  const next = [...prev];
                  next[conflictIndex] = candidateIndex;
                  return next;
                })
              }
              inputProps={{
                "aria-label": `${candidateLabel(candidate, currentShowId)} (${
                  language?.name ?? conflict.languageCode
                })`,
              }}
            />
          }
          label={candidateLabel(candidate, currentShowId)}
          classes={{
            label: clsx(
              classes.conflictCandidateLabel,
              isSelected && classes.conflictCandidateLabelSelected
            ),
          }}
        />
        <Box
          className={clsx(
            classes.conflictCard,
            isSelected && classes.conflictCardSelected
          )}
        >
          <FlexBox alignCenter gap={1} className={classes.conflictLanguage}>
            <CountryFlag flagEmoji={language?.flagEmoji} />
            <Typography variant="body2">
              {language?.name ?? conflict.languageCode}
            </Typography>
          </FlexBox>
          <Box className={classes.conflictValue}>
            <Typography variant="body2">{candidate.text}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <FfairModal open={open} onClose={onClose} isCalledByAnotherModal>
      <FfairPaper className={classes.conflictPaper}>
        <Typography className={classes.conflictTitle}>
          {TRANSLATION_CONFLICT_TITLE}
        </Typography>

        <Alert
          severity="warning"
          fullWidth
          customIcon={<ReportProblemOutlinedIcon />}
          className={classes.conflictAlert}
        >
          {alert}
        </Alert>

        <FlexBox column gap={2} fullWidth className={classes.conflictEntries}>
          {conflicts.map((conflict, index) => (
            <Box
              key={`${conflict.field}:${conflict.languageCode}`}
              className={classes.conflictEntry}
            >
              {renderHeading(conflict, index)}
              <Box className={classes.conflictCandidates}>
                {conflict.candidates.map((candidate, candidateIndex) =>
                  renderCandidate(conflict, index, candidate, candidateIndex)
                )}
              </Box>
            </Box>
          ))}
        </FlexBox>

        <FlexBox alignCenter gap={1} className={classes.conflictActions}>
          <Button color="primary" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            onClick={merge}
            id="translation-conflict-merge"
          >
            {mergeLabel}
          </Button>
        </FlexBox>
      </FfairPaper>
    </FfairModal>
  );
};

export default TranslationConflictModal;
